// src/pages/website-settings/integrations/index.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  useTheme,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import CloudQueueOutlinedIcon from "@mui/icons-material/CloudQueueOutlined";
import ApiOutlinedIcon from "@mui/icons-material/ApiOutlined";
import SaveIcon from "@mui/icons-material/Save";

import {
  getIntegrationSettings,
  updateIntegrationSettings,
} from "../../services/api"; // adjust path if different

const sections = [
  { id: "smtp", label: "SMTP Settings", icon: <EmailOutlinedIcon /> },
  { id: "captcha", label: "Google reCAPTCHA", icon: <SecurityOutlinedIcon /> },
  { id: "cloudflare", label: "Cloudflare Settings", icon: <CloudQueueOutlinedIcon /> },
  { id: "other", label: "Other / API Keys", icon: <ApiOutlinedIcon /> },
];

export default function IntegrationsSettings() {
  const theme = useTheme();

  // Adjust depending on your Admin Header height
  const HEADER_HEIGHT = 70;

  const [selected, setSelected] = useState("smtp");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    smtpHost: "",
    smtpPort: "",
    smtpUser: "",
    smtpPass: "",
    recaptchaSiteKey: "",
    recaptchaSecret: "",
    recaptchaEnabled: true,
    cloudflareEmail: "",
    cloudflareApiKey: "",
    cloudflareZoneId: "",
    jwtSecret: "",
    telegramToken: "",
    webhookSecret: "",
  });

  // Load settings on mount — keep the async function inside the effect so there are no lint deps
  useEffect(() => {
    let mounted = true;

    async function loadSettings() {
      try {
        setLoading(true);
        const res = await getIntegrationSettings();
        const data = res?.data?.data;
        if (!mounted) return;
        if (data) {
          // map only known fields so we don't accidentally set undefined
          setFormData((prev) => ({
            ...prev,
            smtpHost: data.smtpHost ?? prev.smtpHost,
            smtpPort: data.smtpPort ?? prev.smtpPort,
            smtpUser: data.smtpUser ?? prev.smtpUser,
            smtpPass: data.smtpPass ?? prev.smtpPass,
            recaptchaSiteKey: data.recaptchaSiteKey ?? prev.recaptchaSiteKey,
            recaptchaSecret: data.recaptchaSecret ?? prev.recaptchaSecret,
            recaptchaEnabled:
              typeof data.recaptchaEnabled === "boolean" ? data.recaptchaEnabled : prev.recaptchaEnabled,
            cloudflareEmail: data.cloudflareEmail ?? prev.cloudflareEmail,
            cloudflareApiKey: data.cloudflareApiKey ?? prev.cloudflareApiKey,
            cloudflareZoneId: data.cloudflareZoneId ?? prev.cloudflareZoneId,
            jwtSecret: data.jwtSecret ?? prev.jwtSecret,
            telegramToken: data.telegramToken ?? prev.telegramToken,
            webhookSecret: data.webhookSecret ?? prev.webhookSecret,
          }));
        }
      } catch (err) {
        console.error("Failed to load integration settings", err);
        // optionally show user feedback here
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSettings();

    return () => {
      mounted = false;
    };
  }, []); // no eslint-disable needed

  const handleChange = (key) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Send only fields we manage
      const payload = {
        smtpHost: formData.smtpHost || null,
        smtpPort: formData.smtpPort || null,
        smtpUser: formData.smtpUser || null,
        smtpPass: formData.smtpPass || null,
        recaptchaSiteKey: formData.recaptchaSiteKey || null,
        recaptchaSecret: formData.recaptchaSecret || null,
        recaptchaEnabled: Boolean(formData.recaptchaEnabled),
        cloudflareEmail: formData.cloudflareEmail || null,
        cloudflareApiKey: formData.cloudflareApiKey || null,
        cloudflareZoneId: formData.cloudflareZoneId || null,
        jwtSecret: formData.jwtSecret || null,
        telegramToken: formData.telegramToken || null,
        webhookSecret: formData.webhookSecret || null,
      };

      const res = await updateIntegrationSettings(payload);
      // simple feedback; replace with Snackbar if you have one
      alert((res?.data?.message) || "Settings saved");
      // reload to refresh timestamps/values
      // call getIntegrationSettings() again directly
      try {
        const r2 = await getIntegrationSettings();
        const d2 = r2?.data?.data;
        if (d2) {
          setFormData((prev) => ({
            ...prev,
            smtpHost: d2.smtpHost ?? prev.smtpHost,
            smtpPort: d2.smtpPort ?? prev.smtpPort,
            smtpUser: d2.smtpUser ?? prev.smtpUser,
            smtpPass: d2.smtpPass ?? prev.smtpPass,
            recaptchaSiteKey: d2.recaptchaSiteKey ?? prev.recaptchaSiteKey,
            recaptchaSecret: d2.recaptchaSecret ?? prev.recaptchaSecret,
            recaptchaEnabled:
              typeof d2.recaptchaEnabled === "boolean" ? d2.recaptchaEnabled : prev.recaptchaEnabled,
            cloudflareEmail: d2.cloudflareEmail ?? prev.cloudflareEmail,
            cloudflareApiKey: d2.cloudflareApiKey ?? prev.cloudflareApiKey,
            cloudflareZoneId: d2.cloudflareZoneId ?? prev.cloudflareZoneId,
            jwtSecret: d2.jwtSecret ?? prev.jwtSecret,
            telegramToken: d2.telegramToken ?? prev.telegramToken,
            webhookSecret: d2.webhookSecret ?? prev.webhookSecret,
          }));
        }
      } catch (err) {
        // ignore reload error
      }
    } catch (err) {
      console.error("Failed to save integration settings", err);
      alert(err?.response?.data?.error || err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const sidebarItemSx = (active) => ({
    px: 2,
    py: 1.5,
    borderRadius: 1,
    color: active ? "#fff" : "rgba(255,255,255,0.65)",
    background: active
      ? "linear-gradient(90deg, rgba(255,193,7,0.12), rgba(255,193,7,0.05))"
      : "transparent",
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
    },
  });

  const inputSx = {
    "& .MuiFilledInput-root": {
      background: "transparent",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: 1,
    },
    "& .MuiFilledInput-input": { py: 1.5, color: "#fff" },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
  };

  return (
    <Box
      sx={{
        minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
        pt: `${HEADER_HEIGHT + 24}px`, // ensure space below fixed header
        px: 3,
        pb: 5,
        backgroundColor: theme.palette.background.default, // unified background
      }}
    >
      <Box
        sx={{
          maxWidth: 1300,
          mx: "auto",
          display: "flex",
          gap: 3,
          alignItems: "flex-start",
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* Sidebar */}
        <Paper
          elevation={3}
          sx={{
            width: 300,
            bgcolor: "#161b1f",
            color: "rgba(255,255,255,0.8)",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Integrations
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Configure keys & toggles
            </Typography>
          </Box>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

          <List disablePadding>
            {sections.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => setSelected(item.id)}
                selected={selected === item.id}
                sx={sidebarItemSx(selected === item.id)}
              >
                <ListItemIcon sx={{ color: "warning.main", minWidth: 38 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={<Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* Main Content */}
        <Paper
          elevation={3}
          sx={{
            flex: 1,
            borderRadius: 2,
            bgcolor: "#1e2428",
            p: { xs: 2, md: 4 },
            minHeight: 520,
            boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {sections.find((s) => s.id === selected)?.label}
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
                Update credentials and toggles for {sections.find((s) => s.id === selected)?.label}.
              </Typography>
            </Box>
            <Button
              startIcon={<SaveIcon />}
              onClick={handleSave}
              sx={{
                bgcolor: "warning.main",
                color: "black",
                "&:hover": { bgcolor: "warning.dark" },
                fontWeight: 600,
              }}
            >
              Save
            </Button>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 3 }} />

          <Box sx={{ maxWidth: 700 }}>
            {selected === "smtp" && (
              <>
                <TextField label="SMTP Host" fullWidth margin="normal" variant="filled"
                  value={formData.smtpHost} onChange={handleChange("smtpHost")} sx={inputSx} />
                <TextField label="SMTP Port" fullWidth margin="normal" variant="filled"
                  value={formData.smtpPort} onChange={handleChange("smtpPort")} sx={inputSx} />
                <TextField label="SMTP Username" fullWidth margin="normal" variant="filled"
                  value={formData.smtpUser} onChange={handleChange("smtpUser")} sx={inputSx} />
                <TextField label="SMTP Password" type="password" fullWidth margin="normal" variant="filled"
                  value={formData.smtpPass} onChange={handleChange("smtpPass")} sx={inputSx} />
                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <Button variant="contained" sx={{ bgcolor: "warning.main", color: "black", "&:hover": { bgcolor: "warning.dark" }, fontWeight: 600 }} onClick={handleSave}>
                    Save SMTP Settings
                  </Button>
                  <Button variant="outlined" sx={{ color: "#ffc107", borderColor: "#ffc107" }} onClick={() => alert("Send test mail")}>
                    Send Test Mail
                  </Button>
                </Box>
              </>
            )}

            {selected === "captcha" && (
              <>
                <FormControlLabel control={<Switch checked={formData.recaptchaEnabled} onChange={handleChange("recaptchaEnabled")} />} label="Enable reCAPTCHA" sx={{ mb: 2 }} />
                <TextField label="Site Key" fullWidth margin="normal" variant="filled"
                  value={formData.recaptchaSiteKey} onChange={handleChange("recaptchaSiteKey")} sx={inputSx} />
                <TextField label="Secret Key" fullWidth margin="normal" variant="filled"
                  value={formData.recaptchaSecret} onChange={handleChange("recaptchaSecret")} sx={inputSx} />
                <Button variant="contained" sx={{ mt: 3, bgcolor: "warning.main", color: "black", "&:hover": { bgcolor: "warning.dark" }, fontWeight: 600 }} onClick={handleSave}>
                  Save reCAPTCHA Settings
                </Button>
              </>
            )}

            {selected === "cloudflare" && (
              <>
                <TextField label="Cloudflare Email" fullWidth margin="normal" variant="filled"
                  value={formData.cloudflareEmail} onChange={handleChange("cloudflareEmail")} sx={inputSx} />
                <TextField label="API Key" fullWidth margin="normal" variant="filled"
                  value={formData.cloudflareApiKey} onChange={handleChange("cloudflareApiKey")} sx={inputSx} />
                <TextField label="Zone ID" fullWidth margin="normal" variant="filled"
                  value={formData.cloudflareZoneId} onChange={handleChange("cloudflareZoneId")} sx={inputSx} />
                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <Button variant="contained" sx={{ bgcolor: "warning.main", color: "black", "&:hover": { bgcolor: "warning.dark" }, fontWeight: 600 }} onClick={handleSave}>
                    Save Cloudflare Settings
                  </Button>
                  <Button variant="outlined" sx={{ color: "#ffc107", borderColor: "#ffc107" }} onClick={() => alert("Purge cache")}>
                    Purge Cache
                  </Button>
                </Box>
              </>
            )}

            {selected === "other" && (
              <>
                <TextField label="JWT Secret Key" fullWidth margin="normal" variant="filled"
                  value={formData.jwtSecret} onChange={handleChange("jwtSecret")} sx={inputSx} />
                <TextField label="Telegram Bot Token" fullWidth margin="normal" variant="filled"
                  value={formData.telegramToken} onChange={handleChange("telegramToken")} sx={inputSx} />
                <TextField label="Webhook Secret" fullWidth margin="normal" variant="filled"
                  value={formData.webhookSecret} onChange={handleChange("webhookSecret")} sx={inputSx} />
                <Button variant="contained" sx={{ mt: 3, bgcolor: "warning.main", color: "black", "&:hover": { bgcolor: "warning.dark" }, fontWeight: 600 }} onClick={handleSave}>
                  Save Other Settings
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
